import { Controller } from "@hotwired/stimulus"
import ePub from "epubjs"

const LOCATION_BREAK = 1600

export default class extends Controller {
  static outlets = ["ebooks-reader"]
  static targets = ["slider", "status"]

  static values = {
    contentUrl: String,
    cacheKey: String,
    cacheEnabled: { type: Boolean, default: true },
    labels: Object
  }

  connect() {
    this.locations = null
    this.locationBook = null
    this.requestedPercentage = null
    this.renderedPercentage = null
    this.rendering = false
    this.prepareLocations()
  }

  disconnect() {
    this.locationBook?.destroy()
    this.locationBook = null
  }

  browse() {
    this.requestedPercentage = Number(this.sliderTarget.value)
    if (this.locations && !this.rendering) this.renderLatestLocation()
  }

  sync({ percentage }) {
    this.currentPercentage = percentage
    if (!this.rendering) {
      this.sliderTarget.value = percentage
      this.renderedPercentage = null
    }
  }

  async prepareLocations() {
    this.sliderTarget.disabled = true
    this.showStatus(this.labelsValue.loading)

    try {
      this.locations = this.loadLocations() || await this.generateLocations()
      this.locationSections = this.groupLocationsBySection(this.locations)
      this.sliderTarget.disabled = false
      this.hideStatus()
      if (this.requestedPercentage !== null) await this.renderLatestLocation()
    } catch (error) {
      console.error("Unable to prepare Fast Flip locations", error)
      this.showStatus(this.labelsValue.error)
    }
  }

  loadLocations() {
    if (!this.cacheEnabledValue) return null

    try {
      const cached = localStorage.getItem(this.cacheKeyValue)
      if (!cached) return null

      const locations = JSON.parse(cached)
      if (!this.validLocations(locations)) {
        localStorage.removeItem(this.cacheKeyValue)
        return null
      }
      return locations
    } catch (error) {
      localStorage.removeItem(this.cacheKeyValue)
      console.debug("Unable to load cached Fast Flip locations", error)
      return null
    }
  }

  async generateLocations() {
    const book = ePub(this.contentUrlValue, { openAs: "opf", replacements: "none" })
    this.locationBook = book
    try {
      await book.ready
      const locations = await book.locations.generate(LOCATION_BREAK)
      if (!this.validLocations(locations)) throw new Error("EPUB locations are unavailable")
      if (this.cacheEnabledValue) {
        try {
          localStorage.setItem(this.cacheKeyValue, JSON.stringify(locations))
        } catch (error) {
          console.debug("Unable to cache Fast Flip locations", error)
        }
      }
      return locations
    } finally {
      book.destroy()
      if (this.locationBook === book) this.locationBook = null
    }
  }

  validLocations(locations) {
    if (!Array.isArray(locations) || locations.length === 0) return false

    try {
      return locations.every((value) => {
        const cfi = new ePub.CFI(value)
        return cfi.spinePos >= 0
      })
    } catch (_error) {
      return false
    }
  }

  async renderLatestLocation() {
    this.rendering = true
    try {
      while (this.requestedPercentage !== this.renderedPercentage) {
        const percentage = this.requestedPercentage
        await this.ebooksReaderOutlet.openFastFlipLocation(this.cfiForPercentage(percentage))
        this.renderedPercentage = percentage
      }
    } catch (error) {
      this.requestedPercentage = this.renderedPercentage
      if (this.currentPercentage != null) {
        this.sliderTarget.value = this.currentPercentage
      }
      console.error("Unable to browse EPUB locations", error)
      this.showStatus(this.labelsValue.error)
    } finally {
      this.rendering = false
      if (this.requestedPercentage !== this.renderedPercentage) this.renderLatestLocation()
    }
  }

  cfiForPercentage(percentage) {
    const sectionProgress = percentage / 100 * this.locationSections.length
    const sectionIndex = Math.min(Math.floor(sectionProgress), this.locationSections.length - 1)
    const sectionLocations = this.locationSections[sectionIndex]
    const progressWithinSection = percentage === 100 ? 1 : sectionProgress - sectionIndex
    const locationIndex = Math.round((sectionLocations.length - 1) * progressWithinSection)
    return sectionLocations[locationIndex]
  }

  groupLocationsBySection(locations) {
    return locations.reduce((sections, value) => {
      const spinePosition = new ePub.CFI(value).spinePos
      const currentSection = sections.at(-1)

      if (currentSection?.spinePosition === spinePosition) {
        currentSection.locations.push(value)
      } else {
        sections.push({ spinePosition, locations: [value] })
      }

      return sections
    }, []).map((section) => section.locations)
  }

  showStatus(message) {
    this.statusTarget.textContent = message
    this.statusTarget.hidden = false
  }

  hideStatus() {
    this.statusTarget.hidden = true
  }
}
